import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

export default function DemoModeToggle() {
  const [state, setState] = useState({ demo_mode: false, counts: {} });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const refresh = () => api.get("/demo/status").then((r) => setState(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/demo/seed", { months: 6 });
      toast.success(`Demo data loaded — ${data.counts.invoices} invoices across ${data.seeded_months.length} months`);
      refresh();
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(fmtError(e)); } finally { setBusy(false); setOpen(false); }
  };

  const reset = async () => {
    if (!window.confirm("This will delete ALL brands, providers, invoices, payments, GGR runs, and FX rates. Users are not affected. Continue?")) return;
    setBusy(true);
    try {
      await api.post("/demo/reset");
      toast.success("All demo/data wiped");
      refresh();
      setTimeout(() => window.location.reload(), 600);
    } catch (e) { toast.error(fmtError(e)); } finally { setBusy(false); setOpen(false); }
  };

  const toggle = async () => {
    try {
      const { data } = await api.post("/demo/toggle", { enabled: !state.demo_mode });
      setState((s) => ({ ...s, demo_mode: data.demo_mode }));
    } catch (e) { toast.error(fmtError(e)); }
  };

  return (
    <div className="relative">
      <button
        data-testid="demo-toggle-open"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs transition-all ${state.demo_mode
          ? "bg-[var(--wv-accent-light)] text-[var(--wv-primary)] font-medium"
          : "bg-white/60 text-[var(--wv-text-2)] hover:bg-white/80"}`}
      >
        <Sparkles size={14} strokeWidth={1.8} />
        <span className="flex-1 text-left">Demo mode</span>
        <span className={`inline-block h-4 w-8 rounded-full relative transition-colors ${state.demo_mode ? "bg-[var(--wv-primary)]" : "bg-[var(--wv-text-3)]/40"}`}>
          <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${state.demo_mode ? "left-4" : "left-0.5"}`} />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-2 wv-card p-3 z-30 text-sm">
          <div className="mb-2 text-xs text-[var(--wv-text-2)]">Status: <span className={state.demo_mode ? "text-[var(--wv-primary)] font-medium" : ""}>{state.demo_mode ? "ON" : "OFF"}</span></div>
          <div className="mb-3 text-xs text-[var(--wv-text-3)] font-mono">
            {state.counts.invoices ?? 0} inv · {state.counts.brands ?? 0} brands · {state.counts.providers ?? 0} prov · {state.counts.ggr_runs ?? 0} runs
          </div>
          <button data-testid="demo-seed" disabled={busy} onClick={seed} className="w-full mb-1.5 h-9 wv-btn-primary text-xs font-medium">{busy ? "Working..." : "Load demo data"}</button>
          <button data-testid="demo-toggle" disabled={busy} onClick={toggle} className="w-full mb-1.5 h-9 wv-btn-secondary text-xs">Toggle demo-mode flag</button>
          <button data-testid="demo-reset" disabled={busy} onClick={reset} className="w-full h-9 wv-btn-danger text-xs">Wipe all data</button>
        </div>
      )}
    </div>
  );
}
